import React, { useMemo, useState } from 'react';
import {
  DollarSign,
  Download,
  FileSignature,
  Inbox,
  PhoneCall,
  Receipt,
  UserCheck,
} from 'lucide-react';

import { cn } from '../../../../lib/utils';
import { rangeLabel } from '../../../../lib/dateRange';
import { useFetchLeadsDashboard } from '../../../../services/leads';
import {
  Callout,
  Card,
  Delta,
  FlipCard,
  FlipHint,
  HUES,
  HeroCard,
  Ring,
  Skeleton,
  StatTile,
  StatsBack,
} from '../teams/premium';
import { MiniBars, TrendChart, fmtPctV, labelsFor } from '../teams/teamCharts';
import { currentPeriod, periodRange } from '../teams/teamUi';
import FunnelFilters from './FunnelFilters';
import FunnelSteps from './FunnelSteps';
import { SOURCE_GROUPS } from './sourceGroups';
import SourceTable from './SourceTable';
import DroppedLeadsCard from './DropReasonsCard';
import DirectSignupsCard from './DirectSignupsCard';
import { exportRowsToCsv, fmtCurrency } from './chartTheme';

// The Funnel tab (rebuilt in the /billing/envoy look, Nic 2026-10-25).
// Semantics, in short: funnel, tiles and sources = leads RECEIVED in the
// range and how far they got TO DATE (each step means "got at least this
// far"); speed = first reach-out on those leads. Each card's "i" says
// which one applies.

const TINT = {
  sky: 'bg-gradient-to-br from-sky-400 to-sky-500 text-white shadow-sm shadow-sky-500/25',
  indigo:
    'bg-gradient-to-br from-indigo-400 to-indigo-500 text-white shadow-sm shadow-indigo-500/25',
  violet:
    'bg-gradient-to-br from-violet-400 to-violet-500 text-white shadow-sm shadow-violet-500/25',
  emerald:
    'bg-gradient-to-br from-emerald-400 to-emerald-500 text-white shadow-sm shadow-emerald-500/25',
  orange:
    'bg-gradient-to-br from-orange-400 to-orange-500 text-white shadow-sm shadow-orange-500/25',
  teal: 'bg-gradient-to-br from-teal-400 to-teal-500 text-white shadow-sm shadow-teal-500/25',
};

// Where leads come from: the same six groups, names and colors as the Source
// menu and the Source performance table (sourceGroups.js).
const SOURCES = SOURCE_GROUPS;
const SOURCE_NAME = {
  envoy: 'Envoy',
  'referral-program': 'Referral Program',
  partners: 'Partners',
  events: 'Events',
  website: 'Website',
  manual: 'Manual / Other',
  direct: 'Direct Signups',
};

const BY = {
  day: 'by day',
  week: 'by week',
  month: 'by month',
  year: 'by year',
};
const pct100 = (v) => (v == null ? null : v * 100);

// Dates (Nic 2026-10-25): the page's period, the same one the team tabs
// use: a year, quarter, month, or a custom range (an event's dates arrive as
// one too; `jumped` says so). A period compares with the period before to
// the same point; a custom range with the equal stretch right before it
// (the server's default).
// The Source pick is the page's (`filter` / `onFilter`, shared with the team
// tabs); on its own the tab keeps its own.
const LeadsDashboard = ({
  period = currentPeriod('quarter'),
  jumped = false,
  onWindow = () => {},
  filter: sharedFilter,
  onFilter,
}) => {
  const [ownFilter, setOwnFilter] = useState({ source: '', partnerId: '' });
  const filter = sharedFilter || ownFilter;
  const setFilter = onFilter || setOwnFilter;
  const range = useMemo(() => ({ ...periodRange(period), ...filter }), [period, filter]);

  const params = useMemo(
    () => ({
      start: range.start,
      end: range.end,
      ...(range.prevStart && range.prevEnd
        ? { prevStart: range.prevStart, prevEnd: range.prevEnd }
        : {}),
      ...(range.source ? { source: range.source } : {}),
      // A specific org/event pick — takes precedence over source server-side.
      ...(range.partnerId ? { partnerId: range.partnerId } : {}),
    }),
    [
      range.start,
      range.end,
      range.prevStart,
      range.prevEnd,
      range.source,
      range.partnerId,
    ],
  );

  const { data, error, isLoading, isError, isPreviousData, refetch } =
    useFetchLeadsDashboard(params);

  const kpis = data?.kpis;
  const rates = data?.funnelRates;
  const granularity = data?.range?.granularity || 'day';
  const labels = labelsFor(granularity);
  const cohortGran = data?.cohorts?.granularity || granularity;
  const cohortLabels = labelsFor(cohortGran);
  const trend = (data?.trend || []).map((r) => ({ ...r, key: r.bucket }));
  const cohorts = (data?.cohorts?.buckets || []).map((r) => ({
    ...r,
    key: r.bucket,
  }));
  const rangeText = rangeLabel(range.start, range.end);
  const cohortCaption = `Leads received ${rangeText} and how far they've got to date. Each step counts everyone who reached it or any later step.`;
  const vsTitle = data?.range
    ? `Compared with ${rangeLabel(data.range.prevStart, data.range.prevEnd)}`
    : 'Compared with the period before';
  const vs = 'vs before';
  const filenameSuffix = `${range.start}_${range.end}`;
  const pickedEvent = (data?.filterOptions?.events || []).find(
    (e) => e.partnerId === range.partnerId,
  );
  const sourceText = pickedEvent
    ? pickedEvent.name
    : SOURCE_NAME[range.source] || 'All sources';
  const flipKey = `${range.start}|${range.end}|${range.source}|${range.partnerId}`;

  if (isError) {
    return (
      <Card className="flex flex-col items-center justify-center gap-3 py-16">
        <p className="m-0 text-sm text-slate-500">
          {error?.response?.data?.msg || "Couldn't load the funnel."}
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700"
        >
          Try again
        </button>
      </Card>
    );
  }

  // The Funnel's own filters (the dates live in the page's top bar).
  const toolbar = (
    <FunnelFilters
      start={range.start}
      end={range.end}
      source={range.source}
      partnerId={range.partnerId}
      filterOptions={data?.filterOptions}
      onFilter={setFilter}
      onWindow={onWindow}
      jumped={jumped}
    />
  );

  if (isLoading || !kpis) {
    return (
      <div className="space-y-4">
        {toolbar}
        <Skeleton className="h-56" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[10.25rem]" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Direct Signups picked (Nic 2026-10-26): they were never leads, so there
  // is no funnel to show, only their own card.
  if (range.source === 'direct') {
    return (
      <div className="space-y-4">
        {toolbar}
        <div className={cn('space-y-4 transition-opacity duration-200', isPreviousData && 'opacity-50')}>
          <Card>
            <p className="m-0 text-sm text-slate-500 dark:text-slate-400">
              Direct signups were never leads, so they have no funnel. Everything about them for {rangeText} is
              below. Pick another source to see the lead funnel.
            </p>
          </Card>
          <DirectSignupsCard direct={data.directSignups} rangeText={rangeText} vs={vs} vsTitle={vsTitle} />
        </div>
      </div>
    );
  }

  const money = !!kpis.invoiced;
  const exportSources = () =>
    exportRowsToCsv(
      `leads-trend_${filenameSuffix}`,
      trend.map((r) => ({
        Bucket: r.bucket,
        ...Object.fromEntries(SOURCES.map((s) => [s.name, r[s.key]])),
        Total: r.total,
      })),
    );

  return (
    <div className="space-y-4">
      {toolbar}

      {/* keepPreviousData: the old range stays up, dimmed, while the new one
          loads, so nothing jumps. */}
      <div
        className={cn(
          'space-y-4 transition-opacity duration-200',
          isPreviousData && 'opacity-50',
        )}
      >
        <FlipCard
          label="Funnel scoreboard"
          resetKey={flipKey}
          front={
            <HeroCard hint={<FlipHint />}>
              <div className="flex flex-col items-center gap-6 text-center lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-8 lg:text-left">
                <div className="lg:justify-self-start">
                  <h2 className="mb-0 text-2xl font-bold text-white">
                    Lead Funnel
                  </h2>
                  <div className="mt-1 text-sm leading-relaxed text-slate-500">
                    {sourceText}
                    <br />
                    leads received {rangeText}
                  </div>
                </div>
                <div className="flex items-start justify-center gap-8 lg:justify-self-center">
                  <Ring
                    value={pct100(rates?.contactRate?.pct)}
                    label="Contacted"
                    sub="of leads received"
                  />
                  <Ring
                    value={pct100(rates?.closeRate?.pct)}
                    label="Signed"
                    sub="of leads received"
                  />
                </div>
                <div className="lg:justify-self-end lg:pr-6 lg:text-right">
                  <div className="text-5xl font-bold leading-none tabular-nums text-white xl:text-6xl">
                    {kpis.saSigned.value.toLocaleString('en-US')}
                  </div>
                  <div className="mt-2.5 text-sm font-semibold uppercase tracking-wider text-slate-400">
                    agreements signed
                  </div>
                  <div className="mt-2 flex flex-col items-center gap-1 lg:items-end">
                    <Delta
                      value={kpis.saSigned.value}
                      prev={kpis.saSigned.prev}
                      vs={vs}
                      title={vsTitle}
                      dark
                    />
                    <span className="text-sm tabular-nums text-slate-500">
                      from {kpis.received.value.toLocaleString('en-US')} leads
                    </span>
                  </div>
                </div>
              </div>
            </HeroCard>
          }
          back={
            <HeroCard hint={<FlipHint close />}>
              <div className="flex h-full flex-col">
                <h2 className="mb-0 text-base font-bold text-white">
                  How it&apos;s counted
                </h2>
                <div className="mx-auto flex max-w-3xl flex-1 flex-col justify-center gap-2 text-sm leading-relaxed text-slate-400">
                  <p className="mb-0">
                    <b className="text-slate-200">Which leads:</b> every lead
                    received in the range, followed to today. A lead from the
                    3rd that signs on the 20th counts as signed.
                  </p>
                  <p className="mb-0">
                    <b className="text-slate-200">Each step</b> counts everyone
                    who got at least that far, so the funnel only steps down.
                    Anyone who skipped a step on record is listed under the
                    funnel.
                  </p>
                  <p className="mb-0">
                    <b className="text-slate-200">Changes</b> compare with the
                    same stretch before (
                    {data?.range
                      ? rangeLabel(data.range.prevStart, data.range.prevEnd)
                      : 'the period before'}
                    ).
                  </p>
                </div>
              </div>
            </HeroCard>
          }
        />

        <div>
          <div className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
            {rangeText}
            <span className="ml-2 normal-case tracking-normal text-slate-300">
              Click a tile to see it {BY[granularity]}
            </span>
          </div>
          <div
            className={cn(
              'grid grid-cols-2 gap-3',
              money ? 'lg:grid-cols-3' : 'lg:grid-cols-4',
            )}
          >
            <StatTile
              flipKey={flipKey}
              icon={Inbox}
              tint={TINT.sky}
              accent="text-sky-600"
              label="Leads received"
              value={kpis.received.value}
              info="Every lead that arrived in the range, from any source."
              foot={
                <Delta
                  value={kpis.received.value}
                  prev={kpis.received.prev}
                  vs={vs}
                  title={vsTitle}
                />
              }
              backCaption={`${BY[granularity]}, by source`}
              back={
                <MiniBars
                  series={trend}
                  short={labels.short}
                  long={labels.long}
                  fields={SOURCES.map((s) => ({ ...s, get: (b) => b[s.key] }))}
                />
              }
            />
            <StatTile
              flipKey={flipKey}
              icon={PhoneCall}
              tint={TINT.indigo}
              accent="text-indigo-600"
              label="Contacted"
              value={kpis.contacted.value}
              info="Leads we had a real conversation with, or who got further (signed up) either way."
              foot={
                <Delta
                  value={kpis.contacted.value}
                  prev={kpis.contacted.prev}
                  vs={vs}
                  title={vsTitle}
                />
              }
              backCaption={`by the ${granularity} they arrived`}
              back={
                <MiniBars
                  series={trend}
                  short={labels.short}
                  long={labels.long}
                  fields={[
                    {
                      key: 'contacted',
                      name: 'Contacted',
                      color: HUES.indigo,
                      get: (b) => b.contacted,
                    },
                  ]}
                />
              }
            />
            <StatTile
              flipKey={flipKey}
              icon={UserCheck}
              tint={TINT.violet}
              accent="text-violet-600"
              label="Accounts created"
              value={kpis.accounts.value}
              info="Leads who made their Veritas account."
              foot={
                <Delta
                  value={kpis.accounts.value}
                  prev={kpis.accounts.prev}
                  vs={vs}
                  title={vsTitle}
                />
              }
              backCaption={`by the ${granularity} they arrived`}
              back={
                <MiniBars
                  series={trend}
                  short={labels.short}
                  long={labels.long}
                  fields={[
                    {
                      key: 'account',
                      name: 'Accounts',
                      color: HUES.violet,
                      get: (b) => b.account,
                    },
                  ]}
                />
              }
            />
            <StatTile
              flipKey={flipKey}
              icon={FileSignature}
              tint={TINT.emerald}
              accent="text-emerald-600"
              label="SA signed"
              value={kpis.saSigned.value}
              info="Leads who signed their service agreement."
              foot={
                <Delta
                  value={kpis.saSigned.value}
                  prev={kpis.saSigned.prev}
                  vs={vs}
                  title={vsTitle}
                />
              }
              backCaption={`by the ${granularity} they arrived`}
              back={
                <MiniBars
                  series={trend}
                  short={labels.short}
                  long={labels.long}
                  fields={[
                    {
                      key: 'saSigned',
                      name: 'Signed',
                      color: HUES.emerald,
                      get: (b) => b.saSigned,
                    },
                  ]}
                />
              }
            />
            {money ? (
              <>
                <StatTile
                  flipKey={flipKey}
                  icon={Receipt}
                  tint={TINT.orange}
                  accent="text-orange-600"
                  label="Invoiced"
                  value={fmtCurrency(kpis.invoiced.amount)}
                  info="Everything billed to these leads, open or paid. Finance only."
                  foot={
                    <Delta
                      value={kpis.invoiced.amount}
                      prev={kpis.invoiced.prevAmount}
                      vs={`${kpis.invoiced.value} clients`}
                      title={vsTitle}
                    />
                  }
                  backCaption="clients billed"
                  back={
                    <StatsBack
                      stats={[
                        {
                          label: 'paid',
                          value: kpis.paid?.value || 0,
                          color: HUES.emerald,
                        },
                        {
                          label: 'not paid yet',
                          value: Math.max(
                            0,
                            kpis.invoiced.value - (kpis.paid?.value || 0),
                          ),
                          color: HUES.orange,
                        },
                      ]}
                    />
                  }
                />
                <StatTile
                  flipKey={flipKey}
                  icon={DollarSign}
                  tint={TINT.teal}
                  accent="text-teal-600"
                  label="Paid"
                  value={fmtCurrency(kpis.paid.amount)}
                  info="What these leads have paid so far. Finance only."
                  foot={
                    <Delta
                      value={kpis.paid.amount}
                      prev={kpis.paid.prevAmount}
                      vs={`${kpis.paid.value} clients`}
                      title={vsTitle}
                    />
                  }
                  backCaption="clients billed"
                  back={
                    <StatsBack
                      stats={[
                        {
                          label: 'paid',
                          value: kpis.paid.value,
                          color: HUES.emerald,
                        },
                        {
                          label: 'not paid yet',
                          value: Math.max(
                            0,
                            kpis.invoiced.value - kpis.paid.value,
                          ),
                          color: HUES.orange,
                        },
                      ]}
                    />
                  }
                />
              </>
            ) : null}
          </div>
        </div>

        <FunnelSteps
          funnel={data.funnel || []}
          caption={cohortCaption}
          filenameSuffix={filenameSuffix}
        />

        {/* Outside the funnel on purpose (Nic 2026-10-26): direct signups
            were never leads. Shown for all sources only. */}
        <DirectSignupsCard direct={data.directSignups} rangeText={rangeText} vs={vs} vsTitle={vsTitle} />

        {/* Each week's leads, how they converted (the V3 deck's idea). */}
        <Card
          eyebrow={`Conversion · ${BY[cohortGran]}`}
          title="How each batch of leads converted"
          info={`For the leads that arrived each ${cohortGran}: the share contacted, with an account, and signed so far. Gray bars are how many arrived, so you can tell a big week from a small one. The latest is lighter: those leads are still moving.`}
        >
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[13rem_1fr]">
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3 lg:grid-cols-1 lg:content-start">
              <Callout
                color={HUES.sky}
                label="Contact rate"
                value={fmtPctV(pct100(rates?.contactRate?.pct))}
              >
                <Delta
                  value={pct100(rates?.contactRate?.pct)}
                  prev={pct100(rates?.contactRate?.prevPct)}
                  kind="pts"
                  vs={vs}
                  title={vsTitle}
                />
              </Callout>
              <Callout
                color={HUES.indigo}
                label="Contacted to signed"
                value={fmtPctV(pct100(rates?.contactToClose?.pct))}
              >
                <Delta
                  value={pct100(rates?.contactToClose?.pct)}
                  prev={pct100(rates?.contactToClose?.prevPct)}
                  kind="pts"
                  vs={vs}
                  title={vsTitle}
                />
              </Callout>
              <Callout
                color={HUES.emerald}
                label="Close rate"
                value={fmtPctV(pct100(rates?.closeRate?.pct))}
              >
                <Delta
                  value={pct100(rates?.closeRate?.pct)}
                  prev={pct100(rates?.closeRate?.prevPct)}
                  kind="pts"
                  vs={vs}
                  title={vsTitle}
                />
              </Callout>
            </div>
            <TrendChart
              series={cohorts}
              short={cohortLabels.short}
              long={cohortLabels.long}
              leftDomain={[0, 100]}
              leftFormat={(v) => `${v}%`}
              fields={[
                {
                  key: 'received',
                  name: 'Leads received',
                  color: '#E2E8F0',
                  axis: 'right',
                  get: (b) => b.received,
                  axisNote: 'right',
                },
                {
                  key: 'contactPct',
                  name: 'Contacted',
                  color: HUES.sky,
                  type: 'line',
                  get: (b) => b.contactPct,
                  format: fmtPctV,
                },
                {
                  key: 'accountPct',
                  name: 'Account',
                  color: HUES.violet,
                  type: 'line',
                  get: (b) => b.accountPct,
                  format: fmtPctV,
                },
                {
                  key: 'signPct',
                  name: 'Signed',
                  color: HUES.emerald,
                  type: 'line',
                  get: (b) => b.signPct,
                  format: fmtPctV,
                },
              ]}
            />
          </div>
        </Card>

        {/* Response speed left the Funnel (Nic 2026-10-25): speed to lead
            lives on the Sales Team tab; the Funnel shows what the team tabs
            don't. */}
        <Card
          eyebrow={`Leads received · ${BY[granularity]}`}
          title="Where they came from"
          info="New leads per period, by source."
          right={
            <button
              type="button"
              onClick={exportSources}
              aria-label="Export this card's data (CSV)"
              title="Export this card's data (CSV)"
              className="rounded-lg p-1.5 text-slate-400 transition-[color,background-color] hover:bg-slate-100 hover:text-slate-700"
            >
              <Download className="h-4 w-4" />
            </button>
          }
        >
          <TrendChart
            series={trend}
            short={labels.short}
            long={labels.long}
            height={230}
            fields={SOURCES.map((s) => ({
              ...s,
              stack: 'src',
              get: (b) => b[s.key],
            }))}
          />
        </Card>

        <SourceTable
          sources={data.sources || []}
          caption={cohortCaption}
          filenameSuffix={filenameSuffix}
        />

        {/* The old "Current pipeline" card is gone (Nic 2026-10-25): it
            described the hand-off flow that no longer exists. */}
        <DroppedLeadsCard
          drops={data.drops || []}
          received={kpis.received.value || 0}
          filenameSuffix={filenameSuffix}
        />
      </div>
    </div>
  );
};

export default LeadsDashboard;
